import React from 'react';
import { motion } from 'framer-motion';
import { Globe, Heart, Shield, Target, TrendingUp, Users } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: Target,
      title: 'Clarity',
      description: 'We keep every engagement structured around clear priorities and calm decision-making.'
    },
    {
      icon: Heart,
      title: 'Integrity',
      description: 'Trust is built through transparent advice and responsible stewardship.'
    },
    {
      icon: Shield,
      title: 'Steadiness',
      description: 'We help clients move forward with confidence in changing markets.'
    },
    {
      icon: Users,
      title: 'Partnership',
      description: 'Strong relationships and collaborations sit at the heart of every successful outcome.'
    },
    {
      icon: Globe,
      title: 'Perspective',
      description: 'Our work is grounded in a broad view of global opportunity and local realities.'
    },
    {
      icon: TrendingUp,
      title: 'Growth',
      description: 'We support lasting progress for clients, communities, and institutions.'
    }
  ];

  const milestones = [
    { year: '2015', title: 'Founded', description: 'AFB began with a clear mission: connect government infrastructure with fund raising. ie Social Infrastructure projects like Schools and Hospitals etc' },
    { year: '2018', title: 'Regional reach', description: 'Our work expanded across strategic markets and cross-border partnerships.' },
    { year: '2021', title: 'Digital progress', description: 'We strengthened our approach with better insight, tools, and delivery.' },
    { year: '2025', title: 'Continued growth', description: 'We are expanding support for inclusive finance, infrastructure, and enterprise growth.' }
  ];

  const cards = [
    { title: 'Mission', text: 'Connecting Global Investors with African Opportunities' },
    { title: 'Vision', text: 'To become a trusted bridge between opportunities and progress across Africa and beyond.' }
  ];

  const team = [
    {
      name: 'Samuel A. Opoku',
      role: 'Founder & Executive Chairman',
      photo: '/team/a-opoku.jpg',
      bio: 'Sets the direction of the firm and leads the relationships that connect global investors with opportunities across Africa.'
    },
    {
      name: 'Kwabena Nketia',
      role: 'CFO & Business Strategy',
      bio: 'Oversees financial discipline and long-term strategy, keeping growth measured, transparent, and built to last.'
    },
    {
      name: 'Francis Kontoh',
      role: 'Head of IT Department',
      photo: '/team/francis-kontoh.jpg',
      bio: 'Leads the technology behind our platforms, ensuring our systems stay secure, reliable, and ready to scale.'
    }
  ];

  const initials = (name) =>
    name
      .replace(/^(Mr|Mrs|Ms|Dr)\.?\s+/i, '')
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join('');

  return (
    <div className="min-h-screen bg-[#f8fbff] pt-24">
      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-blue-100 bg-white p-8 shadow-sm sm:p-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-600">About African Financial Bridge</p>
            <h1 className="mt-4 text-4xl font-semibold text-slate-900 sm:text-5xl">A focused partner for financial growth and long-term impact.</h1>
            <p className="mt-6 text-lg leading-8 text-slate-600">
              {/* We work at the intersection of finance, strategy, and development to support meaningful progress for clients and communities. */}
            </p>
          </motion.div>

          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            {cards.map((card, index) => (
              <motion.div key={card.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.08 }} className="rounded-2xl border border-blue-100 bg-blue-50/70 p-8">
                <h2 className="text-2xl font-semibold text-slate-900">{card.title}</h2>
                <p className="mt-4 text-base leading-7 text-slate-600">{card.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-blue-100 bg-white p-8 shadow-sm sm:p-12">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-600">Our values</p>
            <h2 className="mt-3 text-3xl font-semibold text-slate-900 sm:text-4xl">The principles that guide every engagement.</h2>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {values.map((value, index) => (
              <motion.div key={value.title} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.06 }} className="rounded-2xl border border-blue-100 bg-white p-7">
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-600 text-white">
                  <value.icon className="h-5 w-5" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900">{value.title}</h3>
                <p className="mt-3 text-base leading-7 text-slate-600">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-blue-100 bg-blue-50/70 p-8 shadow-sm sm:p-12">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-600">Journey</p>
            <h2 className="mt-3 text-3xl font-semibold text-slate-900 sm:text-4xl">A steady path built on discipline and partnership.</h2>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {milestones.map((milestone, index) => (
              <motion.div key={milestone.year} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: index * 0.06 }} className="rounded-2xl border border-blue-100 bg-white p-7">
                <p className="text-sm font-semibold uppercase tracking-[0.25em] text-blue-600">{milestone.year}</p>
                <h3 className="mt-3 text-xl font-semibold text-slate-900">{milestone.title}</h3>
                <p className="mt-3 text-base leading-7 text-slate-600">{milestone.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 pb-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl rounded-[2rem] border border-blue-100 bg-white p-8 shadow-sm sm:p-12">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-blue-600">Leadership</p>
            <h2 className="mt-3 text-3xl font-semibold text-slate-900 sm:text-4xl">The Team</h2>
            <p className="mt-6 text-lg leading-8 text-slate-600">
              A small, focused group bringing together finance, strategy, and technology.
            </p>
          </div>

          <div className="mt-10 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {team.map((member, index) => (
              <motion.div
                key={member.name}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="overflow-hidden rounded-2xl border border-blue-100 bg-white"
              >
                {member.photo ? (
                  <img
                    src={member.photo}
                    alt={member.name}
                    loading="lazy"
                    className="aspect-[4/5] w-full object-cover object-top"
                  />
                ) : (
                  <div className="flex aspect-[4/5] w-full items-center justify-center bg-blue-50">
                    <span className="text-5xl font-semibold tracking-wide text-blue-600">
                      {initials(member.name)}
                    </span>
                  </div>
                )}

                <div className="p-7">
                  <h3 className="text-xl font-semibold text-slate-900">{member.name}</h3>
                  <p className="mt-1 text-sm font-semibold uppercase tracking-[0.15em] text-blue-600">
                    {member.role}
                  </p>
                  <p className="mt-4 text-base leading-7 text-slate-600">{member.bio}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
